import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';

// Import minimal Uppy styles (for internal components)
import '@uppy/core/css/style.min.css';

// Import plugin Tailwind styles (optional - only needed if using plugin-side Tailwind)
// If your theme handles Tailwind, you can remove this import
import '../css/s3-uploader.css';

/**
 * Image Quality Calculator
 * 
 * Classifies print quality based on pixel dimensions vs print size.
 * Uses DPI thresholds encoded as scaling factors.
 */
class QualityCalculator {
  constructor() {
    // DPI threshold multipliers (~162, ~113, ~77 DPI respectively)
    this.excellentFactor = 15.7;
    this.goodFactor = 22.4;
    this.notBadFactor = 33.1;
  }

  /**
   * Calculate quality classification
   * @param {number} pixelWidth - Image width in pixels
   * @param {number} pixelHeight - Image height in pixels
   * @param {number} printWidthCm - Print width in cm
   * @param {number} printHeightCm - Print height in cm
   * @returns {Object} Quality classification with label, level, progress, and message
   */
  calculate(pixelWidth, pixelHeight, printWidthCm, printHeightCm) {
    // Get longest sides
    const bigPixels = Math.max(pixelWidth, pixelHeight);
    const bigCm = Math.max(printWidthCm, printHeightCm);

    // Calculate scale and thresholds
    const scale = bigPixels / 1000;
    const excellentMaxCm = scale * this.excellentFactor;
    const goodMaxCm = scale * this.goodFactor;
    const notBadMaxCm = scale * this.notBadFactor;

    // Classify quality
    let quality;
    if (excellentMaxCm > bigCm) {
      quality = {
        label: 'Excellent',
        level: 'excellent',
        progress: 100,
        message: 'Your image has plenty of resolution for this print size.',
        icon: '✓'
      };
    } else if (goodMaxCm > bigCm) {
      // Calculate progress within the "good" range
      const progress = 75 + (25 * (goodMaxCm - bigCm) / (goodMaxCm - excellentMaxCm));
      quality = {
        label: 'Good',
        level: 'good',
        progress: Math.round(progress),
        message: 'Good quality print at this size.',
        icon: '✓'
      };
    } else if (notBadMaxCm > bigCm) {
      // Calculate progress within the "not bad" range
      const progress = 50 + (25 * (notBadMaxCm - bigCm) / (notBadMaxCm - goodMaxCm));
      quality = {
        label: 'Acceptable',
        level: 'acceptable',
        progress: Math.round(progress),
        message: 'Acceptable quality. Consider a smaller size for better results.',
        icon: '!'
      };
    } else {
      // Calculate how far into "poor" territory
      const overBy = bigCm / notBadMaxCm;
      const progress = Math.max(10, Math.round(50 / overBy));
      quality = {
        label: 'Low Resolution',
        level: 'poor',
        progress: progress,
        message: 'Image resolution is too low for this size. Reduce print dimensions.',
        icon: '✗'
      };
    }

    // Add max recommended sizes for reference
    quality.maxExcellent = Math.round(excellentMaxCm);
    quality.maxGood = Math.round(goodMaxCm);
    quality.maxAcceptable = Math.round(notBadMaxCm);

    return quality;
  }
}

/**
 * Dimension Calculator for Custom Print Products
 * 
 * Calculates print dimensions in CM based on image aspect ratio.
 * Constraints: Min 20x20cm, Max 180x130cm or 130x180cm
 */
class DimensionCalculator {
  constructor(container) {
    this.container = container;
    this.minSize = 20; // cm
    this.maxWidth = 180; // cm
    this.maxHeight = 130; // cm (can be swapped for portrait)
    this.aspectRatio = null;
    this.imageWidth = null;
    this.imageHeight = null;
    
    this.widthInput = document.getElementById('print-width');
    this.heightInput = document.getElementById('print-height');
    this.sizePrompt = document.getElementById('size-prompt');
    this.qualityIndicator = document.getElementById('quality-indicator');
    this.qualityWarning = document.getElementById('quality-warning');
    this.qualityRecommendBtn = document.getElementById('quality-recommend-btn');
    this.qualityHelpToggle = this.qualityIndicator?.querySelector('.quality-help-toggle');
    this.dimensionWarning = document.getElementById('dimension-warning');
    this._warningTimer = null;
    
    // Initialize quality calculator
    this.qualityCalculator = new QualityCalculator();
    
    if (this.widthInput && this.heightInput) {
      this.bindEvents();
    }

    this.qualityRecommendBtn?.addEventListener('click', () => this.applyRecommendedSize());
    this.qualityHelpToggle?.addEventListener('click', (event) => {
      event.stopPropagation();
      const isOpen = this.qualityIndicator?.classList.toggle('is-help-open') || false;
      this.qualityHelpToggle?.setAttribute('aria-expanded', String(isOpen));
    });

    document.addEventListener('click', (event) => {
      if (!this.qualityIndicator?.classList.contains('is-help-open')) return;
      if (this.qualityIndicator.contains(event.target)) return;

      this.qualityIndicator.classList.remove('is-help-open');
      this.qualityHelpToggle?.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * Set image dimensions and calculate initial print size
   * @param {number} width - Image width in pixels
   * @param {number} height - Image height in pixels
   * @param {Object} [options]
   * @param {boolean} [options.showQuality=true] - Whether to show the quality indicator immediately
   */
  setImageDimensions(width, height, { showQuality = true } = {}) {
    this.imageWidth = width;
    this.imageHeight = height;
    this.aspectRatio = width / height;
    
    // Calculate optimal print size within constraints
    const { printWidth, printHeight } = this.calculateOptimalSize();
    
    this.widthInput.value = printWidth;
    this.heightInput.value = printHeight;
    
    // Update quality indicator (optionally deferred until upload completes)
    if (showQuality) {
      this.updateQuality(printWidth, printHeight);
    }
    
    // Update hidden inputs
    this.updateHiddenInputs(printWidth, printHeight);
    
    // Hide the prompt, show aspect lock info
    if (this.sizePrompt) {
      this.sizePrompt.textContent = '';
    }
    
    // Trigger change event for any listeners
    this.container.dispatchEvent(new CustomEvent('dimensions-calculated', {
      detail: { width: printWidth, height: printHeight, aspectRatio: this.aspectRatio }
    }));
  }

  /**
   * Reveal the quality indicator using current dimensions.
   * Call after an upload completes when setImageDimensions was called with showQuality: false.
   */
  showQualityIndicator() {
    if (!this.imageWidth || !this.imageHeight) return;
    const width = parseFloat(this.widthInput.value) || 0;
    const height = parseFloat(this.heightInput.value) || 0;
    if (width && height) {
      this.updateQuality(width, height);
    }
  }

  /**
   * Show a brief warning below the dimension inputs, then auto-dismiss.
   * @param {string} message - Warning text to display
   * @param {number} [duration=4000] - How long to show the message (ms)
   */
  showDimensionWarning(message, duration = 4000) {
    if (!this.dimensionWarning) return;

    clearTimeout(this._warningTimer);

    this.dimensionWarning.textContent = message;
    this.dimensionWarning.classList.remove('wsp-hidden');
    this.dimensionWarning.style.opacity = '1';

    this._warningTimer = setTimeout(() => {
      this.dimensionWarning.style.opacity = '0';
      setTimeout(() => {
        this.dimensionWarning.classList.add('wsp-hidden');
        this.dimensionWarning.style.opacity = '1';
      }, 300);
    }, duration);
  }

  /**
   * Calculate optimal print size respecting aspect ratio and constraints.
   * Maximises dimensions while keeping the longest side within "Acceptable" quality.
   * Quality is evaluated on the longest side only, so only that side is capped
   * at the Acceptable threshold; the shorter side is only limited by the absolute max.
   */
  calculateOptimalSize() {
    const isLandscape = this.aspectRatio >= 1;
    
    // Max longest-side (cm) that still qualifies as "Acceptable" quality.
    // Math.floor already rounds down; no extra subtraction needed.
    const bigPixels = Math.max(this.imageWidth, this.imageHeight);
    const scale = bigPixels / 1000;
    const acceptableMaxCm = Math.floor(scale * 33.1);
    
    // Absolute max dimensions for the orientation
    const absoluteMaxW = isLandscape ? this.maxWidth : this.maxHeight;
    const absoluteMaxH = isLandscape ? this.maxHeight : this.maxWidth;
    
    let printWidth, printHeight;
    
    if (isLandscape) {
      // Width is the longest side — cap it at the Acceptable threshold
      printWidth = Math.min(absoluteMaxW, acceptableMaxCm);
      printHeight = printWidth / this.aspectRatio;
      
      // If height exceeds absolute max, scale down from height
      if (printHeight > absoluteMaxH) {
        printHeight = absoluteMaxH;
        printWidth = printHeight * this.aspectRatio;
      }
    } else {
      // Height is the longest side — cap it at the Acceptable threshold
      printHeight = Math.min(absoluteMaxH, acceptableMaxCm);
      printWidth = printHeight * this.aspectRatio;
      
      // If width exceeds absolute max, scale down from width
      if (printWidth > absoluteMaxW) {
        printWidth = absoluteMaxW;
        printHeight = printWidth / this.aspectRatio;
      }
    }
    
    // Ensure minimum dimensions
    if (printWidth < this.minSize) {
      printWidth = this.minSize;
      printHeight = printWidth / this.aspectRatio;
    }
    if (printHeight < this.minSize) {
      printHeight = this.minSize;
      printWidth = printHeight * this.aspectRatio;
    }
    
    // Round to whole centimeters
    printWidth = Math.round(printWidth);
    printHeight = Math.round(printHeight);
    
    return { printWidth, printHeight };
  }

  applyRecommendedSize() {
    if (!this.aspectRatio) return;

    const { printWidth, printHeight } = this.calculateOptimalSize();
    this.widthInput.value = printWidth;
    this.heightInput.value = printHeight;
    this.updateHiddenInputs(printWidth, printHeight);
    this.updateQuality(printWidth, printHeight);
  }

  /**
   * Bind input events for user adjustments
   */
  bindEvents() {
    // Width input changes
    this.widthInput.addEventListener('input', (e) => {
      if (!this.aspectRatio) return;
      
      let width = parseFloat(e.target.value) || this.minSize;
      width = this.clampWidth(width);
      
      const height = Math.round(width / this.aspectRatio);
      this.heightInput.value = height;
      
      this.updateHiddenInputs(width, height);
      this.updateQuality(width, height);
    });

    // Height input changes
    this.heightInput.addEventListener('input', (e) => {
      if (!this.aspectRatio) return;
      
      let height = parseFloat(e.target.value) || this.minSize;
      height = this.clampHeight(height);
      
      const width = Math.round(height * this.aspectRatio);
      this.widthInput.value = width;
      
      this.updateHiddenInputs(width, height);
      this.updateQuality(width, height);
    });

    // Validate on blur
    this.widthInput.addEventListener('blur', () => this.validateAndCorrect());
    this.heightInput.addEventListener('blur', () => this.validateAndCorrect());
  }

  /**
   * Clamp width to valid range
   */
  clampWidth(width) {
    const isLandscape = this.aspectRatio >= 1;
    const maxW = isLandscape ? this.maxWidth : this.maxHeight;
    return Math.max(this.minSize, Math.min(width, maxW));
  }

  /**
   * Clamp height to valid range
   */
  clampHeight(height) {
    const isLandscape = this.aspectRatio >= 1;
    const maxH = isLandscape ? this.maxHeight : this.maxWidth;
    return Math.max(this.minSize, Math.min(height, maxH));
  }

  /**
   * Validate and correct dimensions on blur.
   * Shows a brief warning when the entered value was adjusted to meet constraints.
   */
  validateAndCorrect() {
    if (!this.aspectRatio) return;
    
    const rawWidth = parseFloat(this.widthInput.value) || this.minSize;
    const rawHeight = parseFloat(this.heightInput.value) || this.minSize;

    // Clamp values
    let width = this.clampWidth(rawWidth);
    let height = width / this.aspectRatio;
    
    // Check if height is within bounds
    const clampedHeight = this.clampHeight(height);
    if (height !== clampedHeight) {
      height = clampedHeight;
      width = height * this.aspectRatio;
      width = this.clampWidth(width);
    }
    
    // Round to whole centimeters
    width = Math.round(width);
    height = Math.round(height);

    const isLandscape = this.aspectRatio >= 1;
    const maxW = isLandscape ? this.maxWidth : this.maxHeight;
    const maxH = isLandscape ? this.maxHeight : this.maxWidth;

    // Detect if the value changed and explain why
    const prevWidth = Math.round(rawWidth);
    const prevHeight = Math.round(rawHeight);

    if (width !== prevWidth || height !== prevHeight) {
      if (prevWidth < this.minSize || prevHeight < this.minSize) {
        this.showDimensionWarning(
          `Minimum print size is ${this.minSize} cm on each side. Dimensions adjusted to maintain aspect ratio.`
        );
      } else if (prevWidth > maxW || prevHeight > maxH) {
        this.showDimensionWarning(
          `Maximum print size is ${maxW} x ${maxH} cm. Dimensions adjusted to maintain aspect ratio.`
        );
      } else {
        this.showDimensionWarning(
          'Dimensions adjusted to maintain aspect ratio within size limits.'
        );
      }
    }
    
    this.widthInput.value = width;
    this.heightInput.value = height;
    
    this.updateHiddenInputs(width, height);
  }

  /**
   * Update hidden form inputs
   */
  updateHiddenInputs(width, height) {
    this.createOrUpdateHiddenInput('print_width_cm', width);
    this.createOrUpdateHiddenInput('print_height_cm', height);
  }

  /**
   * Create or update a hidden input field
   */
  createOrUpdateHiddenInput(name, value) {
    let input = document.getElementById(`${name}_input`);
    
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.id = `${name}_input`;
      input.name = name;
      
      const form = document.querySelector('.variations_form, form.cart');
      if (form) {
        form.appendChild(input);
      }
    }
    
    input.value = value;
  }


  /**
   * Get Greatest Common Divisor
   */
  getGCD(a, b) {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b) {
      const t = b;
      b = a % b;
      a = t;
    }
    return a;
  }

  /**
   * Update quality indicator based on current dimensions
   */
  updateQuality(printWidth, printHeight) {
    if (!this.qualityIndicator || !this.imageWidth || !this.imageHeight) return;
    
    const quality = this.qualityCalculator.calculate(
      this.imageWidth,
      this.imageHeight,
      printWidth,
      printHeight
    );
    
    this.qualityIndicator.className = `quality-indicator quality-indicator-bar quality-${quality.level}`;
    this.qualityIndicator.classList.remove('wsp-hidden');
    
    const labelEl = this.qualityIndicator.querySelector('.quality-label');
    const messageEl = this.qualityIndicator.querySelector('.quality-message');
    const thresholdsEl = this.qualityIndicator.querySelector('.quality-thresholds');
    const warningTitleEl = this.qualityWarning?.querySelector('.quality-warning-title');
    const warningMessageEl = this.qualityWarning?.querySelector('.quality-warning-message');
    const { printWidth: recommendedWidth, printHeight: recommendedHeight } = this.calculateOptimalSize();
    const recommendedQuality = this.qualityCalculator.calculate(
      this.imageWidth,
      this.imageHeight,
      recommendedWidth,
      recommendedHeight
    );
    const imageResolutionLimited = recommendedQuality.level === 'poor';
    
    if (labelEl) labelEl.textContent = quality.label;
    if (messageEl) messageEl.textContent = quality.message;
    if (thresholdsEl) {
      thresholdsEl.textContent = `Excellent up to ${quality.maxExcellent}cm, Good up to ${quality.maxGood}cm, Acceptable up to ${quality.maxAcceptable}cm on the longest side.`;
    }

    if (this.qualityWarning) {
      const shouldWarn = quality.level === 'poor' || imageResolutionLimited;
      this.qualityWarning.classList.toggle('wsp-hidden', !shouldWarn);
      this.qualityWarning.classList.toggle('quality-poor', quality.level === 'poor' || imageResolutionLimited);

      if (imageResolutionLimited) {
        if (warningTitleEl) warningTitleEl.textContent = 'Image resolution is limited';
        if (warningMessageEl) {
          warningMessageEl.textContent = 'This image may not reach good print quality at the available canvas sizes. Consider uploading a higher resolution image.';
        }
      } else {
        if (warningTitleEl) {
          warningTitleEl.textContent = 'Low resolution warning';
        }
        if (warningMessageEl) warningMessageEl.textContent = quality.message;
      }
    }

    if (this.qualityRecommendBtn) {
      const canRecommend = !imageResolutionLimited
        && (recommendedWidth !== Math.round(printWidth) || recommendedHeight !== Math.round(printHeight));
      this.qualityRecommendBtn.classList.toggle('wsp-hidden', !canRecommend);
      this.qualityRecommendBtn.disabled = !canRecommend;
    }
    
    // Update hidden input for backend
    this.createOrUpdateHiddenInput('print_quality', quality.level);
    
    // Dispatch event for other scripts
    this.container.dispatchEvent(new CustomEvent('quality-updated', {
      detail: quality
    }));
  }

  /**
   * Reset calculator state
   */
  reset() {
    this.aspectRatio = null;
    this.imageWidth = null;
    this.imageHeight = null;
    
    if (this.widthInput) this.widthInput.value = '';
    if (this.heightInput) this.heightInput.value = '';
    if (this.qualityIndicator) {
      this.qualityIndicator.classList.add('wsp-hidden');
    }
    if (this.qualityWarning) {
      this.qualityWarning.classList.add('wsp-hidden');
      this.qualityWarning.classList.remove('quality-poor');
    }
    if (this.sizePrompt) {
      this.sizePrompt.textContent = 'Please input your desired print size:';
    }
    
    // Remove hidden inputs
    document.getElementById('print_width_cm_input')?.remove();
    document.getElementById('print_height_cm_input')?.remove();
    document.getElementById('print_quality_input')?.remove();
  }
}

/**
 * Get image dimensions from a file
 */
function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file.data);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    
    img.src = url;
  });
}

/**
 * Gallery Manager
 *
 * Overwrites the first gallery slide with the user's uploaded image and
 * prevents WooCommerce's variation handler from reverting it.
 *
 * FlexSlider's addSlide/removeSlide API is incompatible with WooCommerce's
 * thumbnail gallery mode (it destroys the control nav), so we manipulate
 * the existing first slide's attributes directly and override the jQuery
 * plugin method $.fn.wc_variations_image_update to block image swaps while
 * a custom image is active.
 */
const GalleryManager = {
  _active: false,
  _imageUrl: null,
  _filename: null,
  _overrideInstalled: false,

  /**
   * Replace the first gallery slide with the uploaded image.
   * @param {string} imageUrl - URL of the uploaded image
   * @param {string} filename - Original filename for alt text
   */
  injectSlide(imageUrl, filename) {
    this._imageUrl = imageUrl;
    this._filename = filename || 'Uploaded image';

    const gallery = document.querySelector('.woocommerce-product-gallery');
    if (!gallery) return;

    // Store on gallery element for the lightbox handler
    gallery.dataset.customImageUrl = imageUrl;
    gallery.dataset.customImageFilename = this._filename;

    const mainImg = gallery.querySelector('.woocommerce-product-gallery__image img, .wp-post-image');
    const mainLink = gallery.querySelector('.woocommerce-product-gallery__image a');
    const mainFigure = gallery.querySelector('.woocommerce-product-gallery__image');

    if (mainImg) {
      // Preserve originals for reset (only once)
      if (!mainImg.dataset.wscpOriginalSrc) {
        mainImg.dataset.wscpOriginalSrc = mainImg.getAttribute('src') || '';
        mainImg.dataset.wscpOriginalSrcset = mainImg.getAttribute('srcset') || '';
        mainImg.dataset.wscpOriginalAlt = mainImg.getAttribute('alt') || '';
      }
      mainImg.src = imageUrl;
      mainImg.srcset = '';
      mainImg.alt = this._filename;
      mainImg.classList.add('custom-uploaded-image');
    }

    if (mainLink) {
      if (!mainLink.dataset.wscpOriginalHref) {
        mainLink.dataset.wscpOriginalHref = mainLink.getAttribute('href') || '';
      }
      mainLink.href = imageUrl;
    }

    if (mainFigure) {
      if (!mainFigure.dataset.wscpOriginalThumb) {
        mainFigure.dataset.wscpOriginalThumb = mainFigure.getAttribute('data-thumb') || '';
      }
      mainFigure.setAttribute('data-thumb', imageUrl);
      mainFigure.setAttribute('data-custom-upload', 'true');
    }

    // Update the first thumbnail in the FlexSlider nav.
    // Clear srcset so the browser uses our src, and set data-o_src so
    // WooCommerce's wc_reset_variation_attr doesn't revert the thumbnail.
    const firstThumb = gallery.querySelector('.flex-control-thumbs li:first-child img');
    if (firstThumb) {
      if (!firstThumb.dataset.wscpOriginalSrc) {
        firstThumb.dataset.wscpOriginalSrc = firstThumb.getAttribute('src') || '';
        firstThumb.dataset.wscpOriginalSrcset = firstThumb.getAttribute('srcset') || '';
        firstThumb.dataset.wscpOriginalOSrc = firstThumb.getAttribute('data-o_src') || '';
        firstThumb.dataset.wscpOriginalOSrcset = firstThumb.getAttribute('data-o_srcset') || '';
      }
      firstThumb.src = imageUrl;
      firstThumb.removeAttribute('srcset');
      firstThumb.setAttribute('data-o_src', imageUrl);
      firstThumb.removeAttribute('data-o_srcset');
    }

    // Navigate FlexSlider to slide 0
    if (typeof jQuery !== 'undefined') {
      const flexslider = jQuery(gallery).data('flexslider');
      if (flexslider) {
        flexslider.flexAnimate(0);
      }
    }

    this._active = true;

    // Install the $.fn override once
    this._installOverride();

    document.dispatchEvent(new CustomEvent('product-image-updated', {
      detail: { imageUrl, fileName: filename }
    }));
  },

  /**
   * Restore the first gallery slide to its original product image.
   */
  removeSlide() {
    if (!this._active) return;

    const gallery = document.querySelector('.woocommerce-product-gallery');
    if (gallery) {
      delete gallery.dataset.customImageUrl;
      delete gallery.dataset.customImageFilename;
    }

    const mainImg = gallery?.querySelector('.woocommerce-product-gallery__image img.custom-uploaded-image');
    if (mainImg && mainImg.dataset.wscpOriginalSrc) {
      mainImg.src = mainImg.dataset.wscpOriginalSrc;
      mainImg.srcset = mainImg.dataset.wscpOriginalSrcset || '';
      mainImg.alt = mainImg.dataset.wscpOriginalAlt || '';
      mainImg.classList.remove('custom-uploaded-image');
    }

    const mainLink = gallery?.querySelector('.woocommerce-product-gallery__image a');
    if (mainLink && mainLink.dataset.wscpOriginalHref) {
      mainLink.href = mainLink.dataset.wscpOriginalHref;
    }

    const mainFigure = gallery?.querySelector('.woocommerce-product-gallery__image[data-custom-upload]');
    if (mainFigure) {
      if (mainFigure.dataset.wscpOriginalThumb) {
        mainFigure.setAttribute('data-thumb', mainFigure.dataset.wscpOriginalThumb);
      }
      mainFigure.removeAttribute('data-custom-upload');
    }

    const firstThumb = gallery?.querySelector('.flex-control-thumbs li:first-child img');
    if (firstThumb && firstThumb.dataset.wscpOriginalSrc) {
      firstThumb.src = firstThumb.dataset.wscpOriginalSrc;
      if (firstThumb.dataset.wscpOriginalSrcset) {
        firstThumb.setAttribute('srcset', firstThumb.dataset.wscpOriginalSrcset);
      }
      if (firstThumb.dataset.wscpOriginalOSrc) {
        firstThumb.setAttribute('data-o_src', firstThumb.dataset.wscpOriginalOSrc);
      }
      if (firstThumb.dataset.wscpOriginalOSrcset) {
        firstThumb.setAttribute('data-o_srcset', firstThumb.dataset.wscpOriginalOSrcset);
      }
    }

    this._active = false;
    this._imageUrl = null;
    this._filename = null;
  },

  /**
   * Re-apply the uploaded image to the first slide.
   * Called after WooCommerce's variation handler overwrites it.
   */
  _reapply() {
    if (!this._active || !this._imageUrl) return;

    const gallery = document.querySelector('.woocommerce-product-gallery');
    if (!gallery) return;

    const mainImg = gallery.querySelector('.woocommerce-product-gallery__image img, .wp-post-image');
    const mainLink = gallery.querySelector('.woocommerce-product-gallery__image a');
    const firstThumb = gallery.querySelector('.flex-control-thumbs li:first-child img');

    if (mainImg) {
      mainImg.src = this._imageUrl;
      mainImg.srcset = '';
      mainImg.classList.add('custom-uploaded-image');
    }
    if (mainLink) {
      mainLink.href = this._imageUrl;
    }
    if (firstThumb) {
      firstThumb.src = this._imageUrl;
      firstThumb.removeAttribute('srcset');
      firstThumb.setAttribute('data-o_src', this._imageUrl);
      firstThumb.removeAttribute('data-o_srcset');
    }

    // Navigate back to slide 0
    const flexslider = jQuery(gallery).data('flexslider');
    if (flexslider) {
      flexslider.flexAnimate(0);
    }
  },

  /**
   * Override $.fn.wc_variations_image_update to block WooCommerce from
   * swapping the gallery image while a custom upload is active.
   * This is the correct interception point -- WooCommerce calls
   * $form.wc_variations_image_update(variation) which is a jQuery plugin
   * method on $.fn, not a method on the gallery data object.
   */
  _installOverride() {
    if (this._overrideInstalled) return;
    if (typeof jQuery === 'undefined') return;

    const self = this;
    const origUpdate = jQuery.fn.wc_variations_image_update;
    const origReset = jQuery.fn.wc_variations_image_reset;

    if (origUpdate) {
      jQuery.fn.wc_variations_image_update = function (variation) {
        if (self._active) return this;
        return origUpdate.apply(this, arguments);
      };
    }

    if (origReset) {
      jQuery.fn.wc_variations_image_reset = function () {
        if (self._active) return this;
        return origReset.apply(this, arguments);
      };
    }

    this._overrideInstalled = true;
  },

  /**
   * Whether a custom uploaded image is currently active.
   * @returns {boolean}
   */
  hasSlide() {
    return this._active;
  }
};

/**
 * Initialize S3 File Upload with Uppy
 * 
 * Features:
 * - Direct-to-S3 uploads using presigned URLs
 * - Simple upload button (no dashboard)
 * - Auto-upload on file selection
 * - Automatic dimension calculation based on image aspect ratio
 * - Product gallery image replacement
 */
const getPricingConfig = () => {
  if (typeof window === 'undefined' || !window.WSCP_PRICING) return null;
  const config = window.WSCP_PRICING || {};
  if (!config.enabled) return null;
  return config;
};

const applyPriceRounding = (price, rounding) => {
  switch (rounding) {
    case 'ceil':
      return Math.ceil(price);
    case 'floor':
      return Math.floor(price);
    case 'round':
      return Math.round(price);
    case 'round_05':
      return Math.round(price * 20) / 20;
    case 'round_10':
      return Math.round(price * 10) / 10;
    case 'round_50':
      return Math.round(price * 2) / 2;
    default:
      return Math.round(price * 100) / 100;
  }
};

const resolveProfile = (config, productType, pricingProfile) => {
  const profiles = config.pricing_profiles || {};
  const typeKey = (productType || config.default_product_type || 'canvas').toString().toLowerCase();
  const profileKey = (pricingProfile || config.default_pricing_profile || 'default').toString().toLowerCase();

  if (profiles[typeKey] && profiles[typeKey][profileKey]) {
    return profiles[typeKey][profileKey];
  }
  if (profiles[typeKey] && profiles[typeKey].default) {
    return profiles[typeKey].default;
  }
  if (profiles.canvas && profiles.canvas[profileKey]) {
    return profiles.canvas[profileKey];
  }
  if (profiles.canvas && profiles.canvas.default) {
    return profiles.canvas.default;
  }

  // Legacy fallback from top-level fields.
  const fallback = {
    base_fee: config.base_fee || 0,
    area_rate_per_sq_cm: config.area_rate_per_sq_cm || 0,
    perimeter_rate_per_cm: config.perimeter_rate_per_cm || 0,
    markup: config.markup || 0,
    frame_base_fee: config.frame_base_fee || 0,
    frame_perimeter_rate_per_cm: config.frame_perimeter_rate_per_cm || config.frame_rate_per_linear_cm || 0,
    minimum_price: config.minimum_price || 0,
    price_rounding: config.price_rounding || 'none',
    breakpoints: {},
  };

  if (profileKey === 'default') {
    fallback.frame_base_fee = 0;
    fallback.frame_perimeter_rate_per_cm = 0;
  }

  return fallback;
};

const getBreakpointPrice = (breakpoints, dimension) => {
  const keys = Object.keys(breakpoints)
    .map((k) => parseFloat(k))
    .filter((k) => !Number.isNaN(k))
    .sort((a, b) => a - b);
  if (keys.length === 0) return null;
  let chosen = null;
  for (let i = 0; i < keys.length; i++) {
    if (keys[i] <= dimension) chosen = keys[i];
    else break;
  }
  if (chosen === null) chosen = keys[0];
  return parseFloat(breakpoints[chosen]) || 0;
};

const calculateDynamicPrice = (width, height, behavior, config) => {
  const includeFrame = !!behavior?.includes_frame;
  const productType = behavior?.product_type || config.product_type || config.default_product_type || 'canvas';
  let pricingProfile = behavior?.pricing_profile || config.default_pricing_profile || 'default';
  if (!behavior?.pricing_profile) {
    pricingProfile = includeFrame ? 'framed' : 'default';
  }
  const profile = resolveProfile(config, productType, pricingProfile);

  const breakpoints = profile.breakpoints || {};
  const breakpointKeys = Object.keys(breakpoints).filter((k) => isFinite(parseFloat(k)));
  const hasBreakpoints = breakpointKeys.length > 0;

  let canvasPrice;
  let framePrice = 0;

  if (hasBreakpoints) {
    const dimension = width + height;
    const lookedUp = getBreakpointPrice(breakpoints, dimension);
    canvasPrice = lookedUp !== null ? lookedUp : 0;
  } else {
    const area = width * height;
    const perimeter = (width + height) * 2;
    canvasPrice = (profile.base_fee || 0) + (area * (profile.area_rate_per_sq_cm || 0)) + (perimeter * (profile.perimeter_rate_per_cm || 0));
    if (includeFrame) {
      framePrice = (profile.frame_base_fee || 0) + (perimeter * (profile.frame_perimeter_rate_per_cm || profile.frame_rate_per_linear_cm || 0));
    }
  }

  let subtotal = canvasPrice + framePrice;
  const markup = profile.markup || 0;
  let total = markup > 0 ? subtotal * (1 + markup / 100) : subtotal;

  const minPrice = profile.minimum_price ?? config.minimum_price ?? 0;
  if (minPrice > 0 && total < minPrice) {
    total = minPrice;
  }

  total = applyPriceRounding(total, profile.price_rounding || config.price_rounding);
  return total;
};

const formatPrice = (amount, config) => {
  const decimals = typeof config.price_decimals === 'number' ? config.price_decimals : 2;
  const decimalSeparator = config.price_decimal_separator || '.';
  const thousandSeparator = config.price_thousand_separator || ',';
  const currencySymbol = config.currency_symbol || '$';
  const priceFormat = config.price_format || '%1$s%2$s';

  const parts = amount.toFixed(decimals).split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
  const decimalPart = parts[1] ? decimalSeparator + parts[1] : '';
  const formattedNumber = integerPart + decimalPart;

  return priceFormat
    .replace('%1$s', currencySymbol)
    .replace('%2$s', formattedNumber);
};

const initDynamicPricingButton = (wrapper) => {
  const config = getPricingConfig();
  if (!config) return;

  const form = document.querySelector('form.variations_form');
  if (!form) return;

  const btn = form.querySelector('.single_add_to_cart_button');
  if (!btn) return;

  if (!btn.dataset.defaultText) {
    const rawText = btn.textContent.trim();
    btn.dataset.defaultText = rawText.includes('·')
      ? rawText.split('·')[0].trim()
      : rawText;
  }

  let variationData = null;
  const getPricingBehavior = () => {
    const variationMap = config.variation_behavior_map || {};
    const optionMap = config.option_behavior_map || {};
    const defaultBehavior = {
      includes_frame: false,
      pricing_profile: 'default',
      product_type: config.product_type || config.default_product_type || 'canvas',
    };

    const variationId = variationData?.variation_id?.toString();
    if (variationId && variationMap[variationId]) {
      return {
        ...defaultBehavior,
        ...variationMap[variationId],
      };
    }

    const selectedButton = form.querySelector('.variation-button.selected');
    if (selectedButton) {
      const value = (selectedButton.dataset.value || '').toLowerCase();
      if (value && optionMap[value]) {
        return {
          ...defaultBehavior,
          ...optionMap[value],
        };
      }
    }

    // Legacy fallback for pre-migration products.
    const selects = form.querySelectorAll('select[name^="attribute_"]');
    for (const select of selects) {
      const selectedOption = select.options[select.selectedIndex];
      const label = (selectedOption?.textContent || '').toLowerCase();
      const value = (selectedOption?.value || '').toLowerCase();

      if (!value) {
        continue;
      }

      if (label.includes('canvas only') || value.includes('canvas-only')) {
        return {
          ...defaultBehavior,
          includes_frame: false,
          pricing_profile: 'default',
        };
      }

      if (label.includes('frame') || label.includes('framed') || label.includes('float')) {
        return {
          ...defaultBehavior,
          includes_frame: true,
          pricing_profile: 'framed',
        };
      }
    }

    return defaultBehavior;
  };

  const readDimensions = () => {
    const widthInput = document.getElementById('print-width') || document.querySelector('input[name="print_width_display"]');
    const heightInput = document.getElementById('print-height') || document.querySelector('input[name="print_height_display"]');
    const width = parseFloat(widthInput?.value) || 0;
    const height = parseFloat(heightInput?.value) || 0;
    return { width, height };
  };

  const updateButton = () => {
    const { width, height } = readDimensions();
    if (!width || !height) {
      btn.textContent = btn.dataset.defaultText;
      return;
    }

    const behavior = getPricingBehavior();
    const price = calculateDynamicPrice(width, height, behavior, config);
    if (!price || price <= 0) {
      btn.textContent = btn.dataset.defaultText;
      return;
    }

    const formatted = formatPrice(price, config);
    btn.innerHTML = `${btn.dataset.defaultText} &middot; ${formatted}`;
  };

  const widthInput = document.getElementById('print-width');
  const heightInput = document.getElementById('print-height');
  widthInput?.addEventListener('input', updateButton);
  heightInput?.addEventListener('input', updateButton);

  if (wrapper) {
    wrapper.addEventListener('dimensions-calculated', updateButton);
  }

  if (typeof jQuery !== 'undefined') {
    const $form = jQuery(form);
    $form.on('found_variation', (event, variation) => {
      variationData = variation || null;
      setTimeout(updateButton, 0);
    });
    $form.on('reset_data', () => {
      variationData = null;
      updateButton();
    });
  }

  // Also update on attribute changes
  form.querySelectorAll('select[name^="attribute_"]').forEach((select) => {
    select.addEventListener('change', updateButton);
  });

  setTimeout(updateButton, 150);
};

export const initS3Uploader = () => {
  const uploaderContainer = document.getElementById('s3-uploader');
  
  if (!uploaderContainer) return;

  // Get configuration from data attributes
  const config = {
    maxFiles: parseInt(uploaderContainer.dataset.maxFiles || '1'),
    allowedTypes: uploaderContainer.dataset.allowedTypes?.split(',') || ['image/jpeg', 'image/png', 'image/tiff'],
    maxFileSize: parseInt(uploaderContainer.dataset.maxFileSize || '10485760'),
    apiUrl: uploaderContainer.dataset.apiUrl || '/wp-json/wscp/v1/s3-sign',
    inputName: uploaderContainer.dataset.inputName || 's3_upload_key',
  };

  // Get UI elements
  const uploadOptions = document.getElementById('upload-options');
  const uploadBtn = document.getElementById('upload-trigger-btn');
  const uploadPanel = document.getElementById('upload-panel');
  const uploadDropzone = document.querySelector('.upload-dropzone');
  const urlHelperText = document.getElementById('url-helper-text');
  const urlBtn = document.getElementById('url-trigger-btn');
  const urlInputSection = document.getElementById('url-input-section');
  const urlInput = document.getElementById('image-url-input');
  const urlSubmitBtn = document.getElementById('url-submit-btn');
  const urlCancelBtn = document.getElementById('url-cancel-btn');
  const changeBtn = document.getElementById('change-image-btn');
  const uploadStatus = document.getElementById('upload-status');
  const statusFilename = uploadStatus?.querySelector('.status-filename');
  const progressContainer = uploadStatus?.querySelector('.upload-progress');
  const progressBar = uploadStatus?.querySelector('.progress-bar');
  const loadingIcon = uploadStatus?.querySelector('.status-icon.loading');
  const successIcon = uploadStatus?.querySelector('.status-icon.success');

  // Initialize dimension calculator
  const wrapper = uploaderContainer.closest('#s3-uploader-container');
  const dimensionCalculator = wrapper ? new DimensionCalculator(wrapper) : null;

  // Initialize dynamic pricing button updates
  initDynamicPricingButton(wrapper);

  // Safety net: if WooCommerce somehow bypasses the $.fn override and
  // changes the gallery image, re-apply the uploaded image after a tick.
  if (typeof jQuery !== 'undefined') {
    const $varForm = jQuery('form.variations_form');
    if ($varForm.length) {
      $varForm.on('found_variation reset_data', () => {
        if (!GalleryManager.hasSlide()) return;
        setTimeout(() => GalleryManager._reapply(), 50);
      });
    }
  }

  // Create hidden file input
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = config.allowedTypes.join(',');
  fileInput.style.display = 'none';
  document.body.appendChild(fileInput);

  const hideUrlInput = () => {
    urlInputSection?.classList.add('wsp-hidden');
    urlHelperText?.classList.remove('wsp-hidden');
    urlBtn?.setAttribute('aria-expanded', 'false');
  };

  const showUrlInput = () => {
    urlInputSection?.classList.remove('wsp-hidden');
    urlHelperText?.classList.add('wsp-hidden');
    urlBtn?.setAttribute('aria-expanded', 'true');
    urlInput?.focus();
  };

  // Initialize Uppy (headless - no UI)
  const uppy = new Uppy({
    id: 's3-uploader',
    autoProceed: true,
    allowMultipleUploadBatches: false,
    restrictions: {
      maxNumberOfFiles: config.maxFiles,
      maxFileSize: config.maxFileSize,
      allowedFileTypes: config.allowedTypes,
    },
  });

  // Configure S3 upload
  uppy.use(AwsS3, {
      id: 'AwsS3',
      shouldUseMultipart: false,
      async getUploadParameters(file) {
        const response = await fetch(config.apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: file.name,
            contentType: file.type,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to get upload URL: ${response.statusText}`);
        }

        const data = await response.json();
        
        // Store the S3 key for later use
        uppy.setFileMeta(file.id, { s3Key: data.key });

        return {
          method: 'PUT',
          url: data.url,
          headers: { 'Content-Type': file.type },
        };
      },
    });

  // Handle upload button click
  uploadBtn?.addEventListener('click', () => {
    fileInput.click();
  });

  ['dragenter', 'dragover'].forEach((eventName) => {
    uploadDropzone?.addEventListener(eventName, (event) => {
      event.preventDefault();
      uploadDropzone.classList.add('is-dragging', 'wsp-border-[#315c84]', 'wsp-bg-[#eef6ff]', 'wsp-shadow-[inset_0_0_0_1px_#315c84]');
    });
  });

  ['dragleave', 'drop'].forEach((eventName) => {
    uploadDropzone?.addEventListener(eventName, (event) => {
      event.preventDefault();
      uploadDropzone.classList.remove('is-dragging', 'wsp-border-[#315c84]', 'wsp-bg-[#eef6ff]', 'wsp-shadow-[inset_0_0_0_1px_#315c84]');
    });
  });

  uploadDropzone?.addEventListener('drop', (event) => {
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;

    const transfer = new DataTransfer();
    transfer.items.add(file);
    fileInput.files = transfer.files;
    fileInput.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // Handle change button click
  changeBtn?.addEventListener('click', () => {
    // Clear existing files first
    uppy.cancelAll();
    // Remove the uploaded slide from the gallery
    GalleryManager.removeSlide();
    // Show upload options again
    if (uploadOptions) uploadOptions.classList.remove('wsp-hidden');
    hideUrlInput();
    if (uploadStatus) {
      uploadStatus.classList.add('wsp-hidden');
      uploadStatus.classList.remove('is-uploading');
    }
    // Reset icons for next upload
    if (loadingIcon) loadingIcon.classList.remove('wsp-hidden');
    if (successIcon) successIcon.classList.add('wsp-hidden');
    if (changeBtn) changeBtn.classList.add('wsp-hidden');
  });

  // Handle URL button click - show URL input
  urlBtn?.addEventListener('click', () => {
    showUrlInput();
  });

  // Handle URL cancel button
  urlCancelBtn?.addEventListener('click', () => {
    hideUrlInput();
    if (urlInput) urlInput.value = '';
  });

  // Handle URL submit
  urlSubmitBtn?.addEventListener('click', () => {
    loadImageFromUrl();
  });

  // Handle Enter key in URL input
  urlInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      loadImageFromUrl();
    }
  });

  // Function to load image from URL (server-side fetch to avoid CORS issues)
  async function loadImageFromUrl() {
    const url = urlInput?.value?.trim();
    if (!url) {
      showUploadFeedback('error', 'Please enter an image URL');
      return;
    }

    // Validate URL format
    let parsedUrl;
    try {
      parsedUrl = new URL(url);
    } catch {
      showUploadFeedback('error', 'Please enter a valid URL');
      return;
    }

    // Client-side HTTPS check (server enforces this too)
    if (parsedUrl.protocol !== 'https:') {
      showUploadFeedback('error', 'Only HTTPS URLs are allowed for security');
      return;
    }

    // Show loading state
    if (urlSubmitBtn) {
      urlSubmitBtn.disabled = true;
      urlSubmitBtn.textContent = 'Loading...';
    }

    // Show uploading state immediately
    if (uploadOptions) uploadOptions.classList.add('wsp-hidden');
    if (urlInputSection) urlInputSection.classList.add('wsp-hidden');
    if (uploadStatus) {
      uploadStatus.classList.remove('wsp-hidden');
      uploadStatus.classList.add('is-uploading');
      if (statusFilename) statusFilename.textContent = 'Fetching image from URL...';
      if (progressContainer) progressContainer.classList.remove('wsp-hidden');
      if (progressBar) progressBar.style.width = '25%'; // Indeterminate progress
      if (loadingIcon) loadingIcon.classList.remove('wsp-hidden');
      if (successIcon) successIcon.classList.add('wsp-hidden');
      if (changeBtn) changeBtn.classList.add('wsp-hidden');
    }

    try {
      // Use server-side endpoint to fetch and upload the image
      // This avoids CORS issues and allows proper security validation
      const apiUrl = uploaderContainer.dataset.apiUrl.replace('/s3-sign', '/s3-upload-from-url');
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle WP_Error responses
        const errorMessage = data.message || data.error || 'Failed to load image from URL';
        throw new Error(errorMessage);
      }

      if (!data.success) {
        throw new Error(data.message || 'Failed to upload image');
      }

      // Update progress
      if (progressBar) progressBar.style.width = '100%';

      // Update hidden inputs with the S3 key (file is already uploaded server-side)
      updateHiddenInput(config.inputName, data.key);
      updateHiddenInput('s3_original_filename', data.filename);

      // Store image dimensions if returned
      if (data.width && data.height) {
        updateHiddenInput('image_original_width', data.width);
        updateHiddenInput('image_original_height', data.height);
        
        // Update dimension calculator
        dimensionCalculator?.setImageDimensions(data.width, data.height);
      }

      // Inject the uploaded image as a new carousel slide
      if (data.url) {
        GalleryManager.injectSlide(data.url, data.filename);
      }

      // Update status UI - show success state
      if (uploadStatus) uploadStatus.classList.remove('is-uploading');
      if (statusFilename) statusFilename.textContent = data.filename;
      if (progressContainer) progressContainer.classList.add('wsp-hidden');
      if (loadingIcon) loadingIcon.classList.add('wsp-hidden');
      if (successIcon) successIcon.classList.remove('wsp-hidden');
      if (changeBtn) changeBtn.classList.remove('wsp-hidden');

      // Clear URL input
      if (urlInput) urlInput.value = '';

      enableAddToCartButton();

    } catch (error) {
      console.error('URL load error:', error);
      showUploadFeedback('error', error.message || 'Failed to load image from URL. Make sure it\'s a direct HTTPS image link.');
      
      // Reset UI on error
      if (uploadOptions) uploadOptions.classList.remove('wsp-hidden');
      showUrlInput();
      if (uploadStatus) {
        uploadStatus.classList.add('wsp-hidden');
        uploadStatus.classList.remove('is-uploading');
      }
      if (progressContainer) progressContainer.classList.add('wsp-hidden');
      if (loadingIcon) loadingIcon.classList.remove('wsp-hidden');
      if (successIcon) successIcon.classList.add('wsp-hidden');
      if (changeBtn) changeBtn.classList.add('wsp-hidden');
      
      // Reset gallery and dimensions on error
      GalleryManager.removeSlide();
      dimensionCalculator?.reset();
    } finally {
      // Reset button
      if (urlSubmitBtn) {
        urlSubmitBtn.disabled = false;
        urlSubmitBtn.textContent = 'Load';
      }
    }
  }

  // Global handler to intercept PhotoSwipe lightbox for the custom uploaded slide
  document.addEventListener('click', function(e) {
    const galleryImage = e.target.closest('.woocommerce-product-gallery__image a');
    if (!galleryImage) return;

    // Only intercept clicks on the custom uploaded slide
    const slide = galleryImage.closest('.woocommerce-product-gallery__image');
    if (!slide || !slide.hasAttribute('data-custom-upload')) return;

    const gallery = galleryImage.closest('.woocommerce-product-gallery');
    if (!gallery || !gallery.dataset.customImageUrl) return;

    e.preventDefault();
    e.stopPropagation();

    openCustomLightbox(gallery.dataset.customImageUrl, gallery.dataset.customImageFilename);
  }, true); // Use capture phase to intercept before PhotoSwipe

  /**
   * Open a simple lightbox for the custom uploaded image
   */
  function openCustomLightbox(imageUrl, title) {
    // Check if custom lightbox overlay exists, create if not
    let overlay = document.getElementById('wscp-custom-upload-lightbox');
    
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'wscp-custom-upload-lightbox';
      overlay.className = 'custom-lightbox-overlay';
      overlay.innerHTML = `
        <button class="custom-lightbox-close" aria-label="Close">&times;</button>
        <img src="" alt="" />
        <div class="custom-lightbox-caption"></div>
      `;
      document.body.appendChild(overlay);

      // Add styles
      const style = document.createElement('style');
      style.textContent = `
        .custom-lightbox-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.95);
          z-index: 999999;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.3s ease, visibility 0.3s ease;
          cursor: zoom-out;
        }
        .custom-lightbox-overlay.active {
          opacity: 1;
          visibility: visible;
        }
        .custom-lightbox-overlay img {
          max-width: 95vw;
          max-height: 90vh;
          object-fit: contain;
          box-shadow: 0 10px 60px rgba(0,0,0,0.5);
        }
        .custom-lightbox-close {
          position: absolute;
          top: 20px;
          right: 20px;
          width: 44px;
          height: 44px;
          background: rgba(255,255,255,0.1);
          border: none;
          border-radius: 50%;
          color: white;
          font-size: 28px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }
        .custom-lightbox-close:hover {
          background: rgba(255,255,255,0.25);
        }
        .custom-lightbox-caption {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          color: white;
          font-size: 14px;
          background: rgba(0,0,0,0.6);
          padding: 10px 20px;
          border-radius: 4px;
          max-width: 80%;
          text-align: center;
          word-break: break-word;
        }
      `;
      document.head.appendChild(style);

      // Close handlers
      overlay.querySelector('.custom-lightbox-close').addEventListener('click', () => {
        overlay.classList.remove('active');
      });
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) overlay.classList.remove('active');
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') overlay.classList.remove('active');
      });
    }

    // Set image and open
    const img = overlay.querySelector('img');
    const caption = overlay.querySelector('.custom-lightbox-caption');
    
    img.src = imageUrl;
    img.alt = title || '';
    caption.textContent = title || '';
    
    overlay.classList.add('active');
  }

  // Handle file selection
  fileInput.addEventListener('change', async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clear any previous files
    uppy.cancelAll();

    // Add file to Uppy
    try {
      uppy.addFile({
        name: file.name,
        type: file.type,
        data: file,
        source: 'Local',
      });
    } catch (error) {
      showUploadFeedback('error', error.message);
    }

    // Reset input for re-selection of same file
    fileInput.value = '';
  });

  // Handle file added - process image dimensions
  uppy.on('file-added', async (file) => {
    // Show uploading state
    if (uploadOptions) uploadOptions.classList.add('wsp-hidden');
    if (urlInputSection) urlInputSection.classList.add('wsp-hidden');
    if (uploadStatus) {
      uploadStatus.classList.remove('wsp-hidden');
      uploadStatus.classList.add('is-uploading');
      if (statusFilename) statusFilename.textContent = `Uploading ${file.name}...`;
      if (progressContainer) progressContainer.classList.remove('wsp-hidden');
      if (progressBar) progressBar.style.width = '0%';
      // Show loading icon, hide success icon and change button
      if (loadingIcon) loadingIcon.classList.remove('wsp-hidden');
      if (successIcon) successIcon.classList.add('wsp-hidden');
      if (changeBtn) changeBtn.classList.add('wsp-hidden');
    }

    // Only process image files
    if (!file.type?.startsWith('image/')) return;
    
    try {
      // Get and set dimensions (defer quality indicator until upload completes)
      const dimensions = await getImageDimensions(file);
      dimensionCalculator?.setImageDimensions(dimensions.width, dimensions.height, { showQuality: false });
      
      // Store original dimensions in file meta
      uppy.setFileMeta(file.id, {
        originalWidth: dimensions.width,
        originalHeight: dimensions.height,
      });
    } catch (error) {
      console.warn('Could not get image dimensions:', error);
    }
  });

  // Handle upload progress
  uppy.on('upload-progress', (file, progress) => {
    const percent = Math.round((progress.bytesUploaded / progress.bytesTotal) * 100);
    if (progressBar) {
      progressBar.style.width = `${percent}%`;
    }
  });

  // Handle successful uploads
  uppy.on('upload-success', async (file) => {
    const s3Key = file.meta.s3Key;
    
    if (s3Key) {
      updateHiddenInput(config.inputName, s3Key);
      updateHiddenInput('s3_original_filename', file.name);
      
      // Store image dimensions if available
      if (file.meta.originalWidth && file.meta.originalHeight) {
        updateHiddenInput('image_original_width', file.meta.originalWidth);
        updateHiddenInput('image_original_height', file.meta.originalHeight);
      }
      
      // Fetch presigned S3 URL for gallery display (avoids blob URL issues with lightbox)
      let galleryImageUrl = URL.createObjectURL(file.data); // blob fallback
      try {
        const previewApiUrl = config.apiUrl.replace('/s3-sign', '/s3-preview-url');
        const previewResponse = await fetch(previewApiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key: s3Key }),
        });
        
        if (previewResponse.ok) {
          const previewData = await previewResponse.json();
          if (previewData.success && previewData.url) {
            galleryImageUrl = previewData.url;
          }
        }
      } catch (error) {
        console.warn('Could not fetch S3 preview URL, using blob fallback:', error);
      }

      // Inject the uploaded image as a new carousel slide
      GalleryManager.injectSlide(galleryImageUrl, file.name);
      
      // Update status UI - show success state
      if (uploadStatus) uploadStatus.classList.remove('is-uploading');
      if (statusFilename) statusFilename.textContent = file.name;
      if (progressContainer) progressContainer.classList.add('wsp-hidden');
      // Hide loading icon, show success icon and change button
      if (loadingIcon) loadingIcon.classList.add('wsp-hidden');
      if (successIcon) successIcon.classList.remove('wsp-hidden');
      if (changeBtn) changeBtn.classList.remove('wsp-hidden');

      // Now that upload is confirmed, reveal the quality indicator
      dimensionCalculator?.showQualityIndicator();
      
      enableAddToCartButton();
    }
  });

  // Handle upload errors
  uppy.on('upload-error', (file, error) => {
    console.error('Upload failed:', file.name, error);
    showUploadFeedback('error', `Failed to upload ${file.name}. Please try again.`);
    
    // Reset UI - show upload options again
    if (uploadOptions) uploadOptions.classList.remove('wsp-hidden');
    hideUrlInput();
    if (uploadStatus) {
      uploadStatus.classList.add('wsp-hidden');
      uploadStatus.classList.remove('is-uploading');
    }
    if (progressContainer) progressContainer.classList.add('wsp-hidden');
    // Reset icons
    if (loadingIcon) loadingIcon.classList.remove('wsp-hidden');
    if (successIcon) successIcon.classList.add('wsp-hidden');
    if (changeBtn) changeBtn.classList.add('wsp-hidden');
    
    // Reset gallery
    GalleryManager.removeSlide();
    dimensionCalculator?.reset();
  });

  // Handle file removal
  uppy.on('file-removed', () => {
    if (uppy.getFiles().length === 0) {
      updateHiddenInput(config.inputName, '');
      updateHiddenInput('s3_original_filename', '');
      updateHiddenInput('image_original_width', '');
      updateHiddenInput('image_original_height', '');
      
      // Reset UI - show upload options again
      if (uploadOptions) uploadOptions.classList.remove('wsp-hidden');
      hideUrlInput();
      if (uploadStatus) {
        uploadStatus.classList.add('wsp-hidden');
        uploadStatus.classList.remove('is-uploading');
      }
      // Reset icons for next upload
      if (loadingIcon) loadingIcon.classList.remove('wsp-hidden');
      if (successIcon) successIcon.classList.add('wsp-hidden');
      if (changeBtn) changeBtn.classList.add('wsp-hidden');
      
      // Reset gallery and dimensions
      GalleryManager.removeSlide();
      dimensionCalculator?.reset();
    }
  });

  // Expose uppy instance for external use
  window.wscpS3Uppy = uppy;
};

/**
 * Update or create hidden input with S3 key.
 */
function updateHiddenInput(name, value) {
  let input = document.getElementById(`${name}_input`);
  
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.id = `${name}_input`;
    input.name = name;
    
    const form = document.querySelector('.variations_form, form.cart');
    if (form) {
      form.appendChild(input);
    }
  }
  
  input.value = value;
  
  // Dispatch custom event for other scripts
  document.dispatchEvent(new CustomEvent('wscp-s3-upload-complete', { 
    detail: { key: value, inputName: name } 
  }));
}

/**
 * Show upload feedback message.
 */
function showUploadFeedback(type, message) {
  const container = document.getElementById('upload-feedback');
  
  if (!container) return;
  
  container.className = `upload-feedback ${type}`;
  container.textContent = message;
  container.classList.remove('wsp-hidden');
  
  if (type === 'success') {
    setTimeout(() => {
      container.classList.add('wsp-hidden');
    }, 5000);
  }
}

/**
 * Enable the add to cart button.
 */
function enableAddToCartButton() {
  const btn = document.querySelector('.single_add_to_cart_button');
  if (btn && btn.dataset.originalText) {
    btn.disabled = false;
    btn.classList.remove('disabled');
    btn.textContent = btn.dataset.originalText;
  }
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initS3Uploader);
} else {
  initS3Uploader();
}
